import { Fragment, useState } from "react";
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { X } from "lucide-react";

import { tw } from "@/utils";
import { Spinner } from "./Spinner";

export interface ComboboxOption {
  id: string;
  label: string;
}

interface CustomComboboxProps {
  options: ComboboxOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  hasError: boolean;
  placeholder: string;
  hasMultipleSelection?: boolean;
  isLoading?: boolean;
}

export function ComboboxWithLabels({
  options,
  value,
  onChange,
  hasError,
  placeholder,
  hasMultipleSelection = false,
  isLoading = false,
}: CustomComboboxProps) {
  const [query, setQuery] = useState("");

  const selectedOption = !Array.isArray(value)
    ? options.find((option) => option.id === value)
    : undefined;

  const selectedOptions = Array.isArray(value)
    ? options.filter((option) => value.includes(option.id))
    : [];

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          const searchString = query.toLowerCase();
          return option.label.toLowerCase().includes(searchString);
        });

  const removeOption = (optionIdToRemove: string) => {
    if (Array.isArray(value)) {
      onChange(value.filter((val) => val !== optionIdToRemove));
    }
  };

  return (
    <div>
      <HeadlessCombobox
        value={
          hasMultipleSelection
            ? selectedOptions
            : (selectedOption ?? { id: "", label: "" })
        }
        onChange={(options: ComboboxOption | ComboboxOption[]) => {
          if (hasMultipleSelection) {
            const optionsArray = options as ComboboxOption[];
            onChange(optionsArray.map((option) => option.id));
          } else {
            const singleOption = options as ComboboxOption;
            onChange(singleOption.id);
          }
        }}
        multiple={hasMultipleSelection}
      >
        <div className="relative">
          <ComboboxInput
            placeholder={placeholder}
            className={tw(
              "h-12 w-full rounded-lg border border-neutral-200 px-4 py-3 shadow-sm",
              "hover:border-primary-200",
              "focus:border-primary-300 focus:shadow-focus focus:outline-none",
              hasError && "border-error-500",
            )}
            displayValue={(option: ComboboxOption | undefined) =>
              option ? option.label : ""
            }
            onChange={(event) => setQuery(event.target.value)}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
            <ChevronDownIcon className="size-4 group-data-[hover]:fill-white" />
          </ComboboxButton>
        </div>
        {hasMultipleSelection && value.length > 0 && Array.isArray(value) && (
          <div className="flex flex-wrap gap-2 pt-4">
            {value.map((selectedId) => {
              const matchedOption = options.find(
                (option) => option.id === selectedId,
              );
              return (
                <div
                  key={selectedId}
                  className="flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-sm text-primary-600"
                >
                  {matchedOption ? matchedOption.label : selectedId}
                  <button
                    type="button"
                    onClick={() => removeOption(selectedId)}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    <div className="rounded bg-primary-100">
                      <X size={12} />
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <ComboboxOptions
          anchor="bottom"
          transition
          className={tw(
            "flex w-[var(--input-width)] flex-col gap-2 rounded-md bg-white px-2 shadow-md [--anchor-gap:var(--spacing-1)] empty:invisible",
            "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
          )}
        >
          {isLoading ? (
            <div className="flex w-full justify-center p-2">
              <Spinner />
            </div>
          ) : (
            filteredOptions.map((option) => (
              <HeadlessCombobox.Option
                key={option.id}
                value={option}
                className={({ active, selected }) =>
                  tw(
                    "flex cursor-default select-none items-center rounded-md py-2 pl-3 pr-8 text-sm text-primary-950 outline-none",
                    active && "bg-primary-50",
                    selected &&
                      "border data-[selected]:border-primary-500 data-[selected]:bg-primary-100",
                  )
                }
              >
                {({ selected }) => (
                  <Fragment>
                    <span className="flex-1">{option.label}</span>
                    {selected && (
                      <CheckIcon
                        className="size-5 text-primary-600"
                        aria-hidden="true"
                      />
                    )}
                  </Fragment>
                )}
              </HeadlessCombobox.Option>
            ))
          )}
        </ComboboxOptions>
      </HeadlessCombobox>
    </div>
  );
}
